/**
 * @file きき分けあそび (epic_24) の進段エンジン。UI から独立した純粋関数群。
 * @why: 段階 (和音の追加)・治療 (隔離練習) の判定を自動化する (代表要件 2026-08-24)。
 *   規則は epic_24 §2 の正典対応表 (R 番号) に従い、原典に無い数値は kikiwakeConfig.ts の
 *   運用値。状態はログから決定的に再構成できる (チェックポイント + リプレイ、§6.4)。
 * @failure: 乱数は rng 注入 (テスト決定化)。日付はすべて YYYY-MM-DD の文字列 (JST 前提、
 *   呼び出し側が todayLocal で渡す)。active は正典順の先頭部分列という不変条件を持ち、
 *   リプレイで縮む場合はチェックポイントとの和集合を採る (R18: 減らさない)。
 */

import { KIKIWAKE_CHORDS, chordOrder, kikiwakeChord, type ChordId } from "./chords";
import { kikiwakeConfig, type KikiwakeConfig } from "./kikiwakeConfig";
import { parseKikiwakeDetail, type KikiwakeState, type KikiwakeTrial } from "./kikiwakeLog";

const CANON: readonly ChordId[] = KIKIWAKE_CHORDS.map((c) => c.id);

/** ログ取得の頁数上限 (limit=100 × 30 頁 = 3000 件 ≈ 1.5 年ぶん)。
 *  @why: 系譜が壊れたときのフルリプレイは全履歴を読むのが理想だが、極端に長い履歴では
 *  リクエスト数とメモリが膨らむ。上限で切っても、チェックポイントとの和集合不変条件
 *  (resolveKikiwakeState) が active の後退を防ぐため、劣化は許容範囲に収まる。 */
export const KIKIWAKE_MAX_FETCH_PAGES = 30;

function utcMs(date: string): number {
  return Date.UTC(Number(date.slice(0, 4)), Number(date.slice(5, 7)) - 1, Number(date.slice(8, 10)));
}

/** YYYY-MM-DD の日数差 (b - a)。文字列日付だけで閉じる (タイムゾーンの揺れを持ち込まない)。 */
export function diffDays(a: string, b: string): number {
  return Math.round((utcMs(b) - utcMs(a)) / 86400000);
}

function addDays(date: string, days: number): string {
  const t = new Date(utcMs(date) + days * 86400000);
  return `${t.getUTCFullYear()}-${String(t.getUTCMonth() + 1).padStart(2, "0")}-${String(t.getUTCDate()).padStart(2, "0")}`;
}

/** 非空配列の末尾 (active などの不変条件を型に伝える)。 */
function lastOf<T>(arr: readonly T[]): T {
  const v = arr[arr.length - 1];
  if (v === undefined) throw new Error("kikiwake: unexpected empty array");
  return v;
}

export function initialKikiwakeState(startDate: string): KikiwakeState {
  // 最初は赤 (ドミソ) だけ (R7、s.81)
  return {
    active: ["domiso"],
    addedAt: { domiso: startDate },
    lastMissDate: null,
    care: null,
    recentDays: {},
    recentMissEvents: [],
    lastFirstChord: null,
  };
}

function counted(trials: KikiwakeTrial[]): KikiwakeTrial[] {
  return trials.filter((t) => !t.n);
}

/** state.recentDays のうち「実施日」(日合計回答数が閾値以上) を昇順で返す。 */
export function practiceDates(state: KikiwakeState, cfg: KikiwakeConfig): string[] {
  return Object.entries(state.recentDays)
    .filter(([, v]) => v.t >= cfg.practiceDayMinTrials)
    .map(([d]) => d)
    .sort();
}

/** 治療の発火判定 (R17)。直近の誤答イベントから最も混同しているペアを 3 ケースで分類する。 */
function judgeCare(state: KikiwakeState, cfg: KikiwakeConfig): KikiwakeState["care"] {
  const dir = new Map<string, number>();
  for (const e of state.recentMissEvents) {
    const key = `${e.c}>${e.x}`;
    dir.set(key, (dir.get(key) ?? 0) + 1);
  }
  let best: { a: ChordId; b: ChordId; aToB: number; bToA: number } | null = null;
  const seen = new Set<string>();
  for (const e of state.recentMissEvents) {
    const swap = chordOrder(e.c) > chordOrder(e.x);
    const a = swap ? e.x : e.c;
    const b = swap ? e.c : e.x;
    const pairKey = `${a}|${b}`;
    if (seen.has(pairKey)) continue;
    seen.add(pairKey);
    const aToB = dir.get(`${a}>${b}`) ?? 0;
    const bToA = dir.get(`${b}>${a}`) ?? 0;
    if (!best || aToB + bToA > best.aToB + best.bToA) best = { a, b, aToB, bToA };
  }
  if (!best || best.aToB + best.bToA < cfg.careTriggerCount) return null;
  const { a, b, aToB, bToA } = best;
  // s.122 の 3 ケース。isolated = 混ぜる練習から外す方
  if (aToB > 0 && bToA > 0 && Math.abs(aToB - bToA) <= cfg.careEqualMaxDiff) {
    // 同程度: 後から練習をはじめた方を隔離 (追加順 = 正典順)
    const later = chordOrder(a) > chordOrder(b) ? a : b;
    return { isolated: later, mixed: later === a ? b : a, kind: "equal", cleanDates: [] };
  }
  if (aToB > 0 && bToA > 0) {
    // 片寄り: より多くまちがえられる方 (出題されて誤答が多い方) を隔離
    const isolated = aToB > bToA ? a : b;
    return { isolated, mixed: isolated === a ? b : a, kind: "asymmetric", cleanDates: [] };
  }
  // 一方的: まちがえられる方を隔離
  const isolated = aToB > 0 ? a : b;
  return { isolated, mixed: isolated === a ? b : a, kind: "one_way", cleanDates: [] };
}

export interface KikiwakeSessionInput {
  date: string;
  trials: KikiwakeTrial[];
}

/**
 * セッション 1 件を状態に適用する (§5)。判定順: 記録の集計 → 治療の復帰/発火 → 追加判定。
 * 自動で和音を減らす経路は無い (R18)。
 */
export function applyKikiwakeSession(
  state: KikiwakeState,
  session: KikiwakeSessionInput,
  cfg: KikiwakeConfig,
): KikiwakeState {
  const next: KikiwakeState = {
    active: [...state.active],
    addedAt: { ...state.addedAt },
    lastMissDate: state.lastMissDate,
    care: state.care ? { ...state.care, cleanDates: [...state.care.cleanDates] } : null,
    recentDays: { ...state.recentDays },
    recentMissEvents: [...state.recentMissEvents],
    lastFirstChord: state.lastFirstChord,
  };
  const date = session.date;
  const ct = counted(session.trials);
  const misses = ct.filter((t) => t.x !== undefined);

  const firstTrial = session.trials[0];
  if (firstTrial !== undefined) {
    next.lastFirstChord = firstTrial.c;
  }
  if (ct.length > 0) {
    const day = next.recentDays[date] ?? { t: 0, m: 0 };
    next.recentDays[date] = { t: day.t + ct.length, m: day.m + misses.length };
  }
  for (const t of misses) {
    next.lastMissDate = next.lastMissDate && next.lastMissDate > date ? next.lastMissDate : date;
    next.recentMissEvents.push({ d: date, c: t.c, x: t.x as ChordId });
  }

  // 30 日より古い日別集計を刈る
  const cutoff = addDays(date, -29);
  for (const d of Object.keys(next.recentDays)) {
    if (d < cutoff) delete next.recentDays[d];
  }
  // 誤答イベントは直近 careWindowPracticeDays 実施日ぶんだけ残す
  const pDates = practiceDates(next, cfg);
  const windowStart =
    (pDates.length >= cfg.careWindowPracticeDays ? pDates[pDates.length - cfg.careWindowPracticeDays] : pDates[0]) ?? date;
  next.recentMissEvents = next.recentMissEvents.filter((e) => e.d >= windowStart);

  if (next.care) {
    // 復帰判定: この実施日に isolated をミスなしでこなせたか (§5.5)
    const isoTrials = ct.filter((t) => t.c === next.care?.isolated);
    if (isoTrials.length > 0) {
      if (isoTrials.some((t) => t.x !== undefined)) {
        next.care.cleanDates = [];
      } else if (!next.care.cleanDates.includes(date)) {
        next.care.cleanDates = [...next.care.cleanDates, date];
      }
      if (next.care.cleanDates.length >= cfg.careRecoverCleanDays) {
        // 復帰: 混ぜる練習へ戻す。重みづけは「追加 7 日相当」から再開する (§5.5)。
        // addedAt を書き換えるのは治療あけの再スタート意味論 (追加直後の集中枠に戻し、
        // 進段の待ち時計もここから数え直す)
        next.addedAt[next.care.isolated] = addDays(date, -7);
        next.care = null;
      }
    }
  } else {
    next.care = judgeCare(next, cfg);
  }

  // 追加判定 (R5〜R7)。治療中は追加しない
  const nextChord = CANON[next.active.length];
  if (!next.care && nextChord !== undefined) {
    const addedDate = next.addedAt[lastOf(next.active)];
    const waited = addedDate !== undefined && diffDays(addedDate, date) >= cfg.addWaitDays;
    const missFree = next.lastMissDate === null || diffDays(next.lastMissDate, date) >= cfg.missFreeDays;
    const windowFrom = addDays(date, -(cfg.missFreeDays - 1));
    const practiced = practiceDates(next, cfg).filter((d) => d >= windowFrom && d <= date).length;
    if (waited && missFree && practiced >= cfg.minPracticeDays) {
      next.active = [...next.active, nextChord];
      next.addedAt[nextChord] = date;
    }
  }
  return next;
}

/** 無音疑い (M13): 全問誤答かつ平均反応が速すぎるセッション。保存前に全 trial を n にする。 */
export function detectMutedSuspect(trials: KikiwakeTrial[], cfg: KikiwakeConfig): boolean {
  if (trials.length === 0) return false;
  if (!trials.every((t) => t.x !== undefined)) return false;
  const times = trials.map((t) => t.ms).filter((ms): ms is number => ms !== undefined);
  if (times.length === 0) return false;
  return times.reduce((a, b) => a + b, 0) / times.length < cfg.fastTapMs;
}

export function markAllNoCount(trials: KikiwakeTrial[]): KikiwakeTrial[] {
  return trials.map((t) => ({ ...t, n: true as const }));
}

/** 記録タブで人が読む 1 行 (AC-6)。カウント対象だけを数える。 */
export function buildKikiwakeMemo(trials: KikiwakeTrial[]): string {
  const ct = counted(trials).filter((t) => !t.k);
  if (ct.length === 0) return "きき分けあそび (きろくなし)";
  const correct = ct.filter((t) => t.x === undefined).length;
  if (correct === ct.length) return `きき分けあそび ${ct.length}問ぜんぶせいかい！`;
  const pairs: string[] = [];
  for (const t of ct) {
    if (t.x === undefined) continue;
    const label = `${kikiwakeChord(t.c).colorKana}→${kikiwakeChord(t.x).colorKana}`;
    if (!pairs.includes(label)) pairs.push(label);
  }
  const shown = pairs.slice(0, 3).join("、") + (pairs.length > 3 ? " など" : "");
  return `きき分けあそび ${ct.length}問中${correct}問せいかい。まちがえたのは ${shown}`;
}

export interface KikiwakeSessionPlan {
  /** 追加した日の導入 (「きいてね」で 3 回鳴らす。回答なし。R20)。 */
  intro: ChordId | null;
  /** 混ぜる出題の列。 */
  plan: ChordId[];
  /** 治療中の最後のかため弾き (s.122〜123: 隔離した和音を練習の最後に)。 */
  katame: ChordId[];
}

function newestShare(daysSinceAdd: number, poolSize: number, cfg: KikiwakeConfig): number {
  const even = 1 / poolSize;
  if (daysSinceAdd >= cfg.newChordEvenDay) return even;
  if (daysSinceAdd <= 0) return cfg.newChordShareDay0;
  if (daysSinceAdd <= 7) {
    return cfg.newChordShareDay0 + (cfg.newChordShareDay7 - cfg.newChordShareDay0) * (daysSinceAdd / 7);
  }
  return cfg.newChordShareDay7 + (even - cfg.newChordShareDay7) * ((daysSinceAdd - 7) / (cfg.newChordEvenDay - 7));
}

/**
 * 1 セッションの出題列を作る (§5.4)。重みづけ (R20)・並びの制約 (R15)・治療の隔離と
 * かため弾き (R17) をここで機械化する。rng は [0,1) を返す注入乱数。
 */
export function buildKikiwakeSession(
  state: KikiwakeState,
  today: string,
  cfg: KikiwakeConfig,
  rng: () => number,
): KikiwakeSessionPlan {
  const pool = state.care ? state.active.filter((c) => c !== state.care?.isolated) : [...state.active];
  const count = Math.min(cfg.maxQuestions, Math.max(cfg.minQuestions, cfg.questionsPerChord * pool.length));
  const newest = lastOf(state.active);
  const weights = new Map<ChordId, number>();
  if (pool.length === 1) {
    weights.set(lastOf(pool), 1);
  } else if (pool.includes(newest)) {
    const days = diffDays(state.addedAt[newest] ?? today, today);
    const share = newestShare(days, pool.length, cfg);
    for (const c of pool) weights.set(c, c === newest ? share : (1 - share) / (pool.length - 1));
  } else {
    for (const c of pool) weights.set(c, 1 / pool.length);
  }

  const pick = (banned: Set<ChordId>): ChordId => {
    const candidates = pool.filter((c) => !banned.has(c));
    const list = candidates.length > 0 ? candidates : pool;
    const total = list.reduce((s, c) => s + (weights.get(c) ?? 0), 0);
    let roll = rng() * total;
    for (const c of list) {
      roll -= weights.get(c) ?? 0;
      if (roll <= 0) return c;
    }
    return lastOf(list);
  };

  const plan: ChordId[] = [];
  for (let i = 0; i < count; i++) {
    const banned = new Set<ChordId>();
    if (pool.length > 1) {
      const p1 = plan[i - 1];
      const p2 = plan[i - 2];
      // 同一和音の 3 連続禁止
      if (p1 !== undefined && p1 === p2) banned.add(p1);
      // 先頭を前セッションと同じにしない
      if (i === 0 && state.lastFirstChord) banned.add(state.lastFirstChord);
      // ABAB の交互を 3 回続けない (ABABA まで来たら次の B を禁止)
      const p3 = plan[i - 3];
      const p4 = plan[i - 4];
      const p5 = plan[i - 5];
      if (p2 !== undefined && p1 === p3 && p3 === p5 && p2 === p4) {
        banned.add(p2);
      }
    }
    plan.push(pick(banned));
  }
  // 全和音を最低 1 回含める (置換先は隣が同じ和音にならない位置。先頭は R15 の
  // 「前回と同じ和音で始めない」を保つため置換しない)
  for (const c of pool) {
    if (plan.includes(c) || plan.length < 2) continue;
    let idx = -1;
    for (let tryN = 0; tryN < 20; tryN++) {
      const i = 1 + Math.floor(rng() * (plan.length - 1));
      if (plan[i - 1] !== c && plan[i + 1] !== c) {
        idx = i;
        break;
      }
    }
    plan[idx >= 0 ? idx : 1 + Math.floor(rng() * (plan.length - 1))] = c;
  }

  const isAddDay = state.addedAt[newest] === today && state.recentDays[today] === undefined;
  return {
    intro: isAddDay && pool.includes(newest) ? newest : null,
    plan,
    katame: state.care ? Array<ChordId>(cfg.katamePlays).fill(state.care.isolated) : [],
  };
}

/** 誤答した和音を同セッションの残りにもう一度混ぜる (§5.4)。残りに無ければ 1 枠を置き換える。 */
export function reinforceAfterMiss(plan: ChordId[], nextIndex: number, chord: ChordId, rng: () => number): ChordId[] {
  const rest = plan.slice(nextIndex);
  if (rest.includes(chord) || rest.length === 0) return plan;
  const out = [...plan];
  for (let tryN = 0; tryN < 20; tryN++) {
    const i = nextIndex + Math.floor(rng() * rest.length);
    if (out[i - 1] !== chord && out[i + 1] !== chord) {
      out[i] = chord;
      return out;
    }
  }
  out[plan.length - 1] = chord;
  return out;
}

export interface KikiwakeLogInput {
  id: string;
  /** サーバの created_at (RFC3339)。リプレイの並べ替えキー (§6.4)。 */
  createdAt: string;
  date: string;
  detail?: string;
}

export interface KikiwakeResolvedState {
  state: KikiwakeState;
  source: "initial" | "checkpoint" | "replay";
  /** 系譜検査 (base/seq) の不一致を検出した (並行セッションの分岐など)。 */
  forked: boolean;
  /** 次のセッション保存に使う通し番号と起点 id。 */
  nextSeq: number;
  baseId: string | null;
}

interface ParsedLog {
  id: string;
  createdAt: string;
  date: string;
  cfg: number;
  seq: number;
  base: string | null;
  trials: KikiwakeTrial[];
  after: KikiwakeState | null;
}

/**
 * ログ列から現在状態を決める (§6.4)。
 * 通常は最新チェックポイント (系譜検査つき)。不一致・欠損はフルリプレイに落ち、
 * リプレイ結果がチェックポイントより短い active を出した場合は長い方 (正典順の
 * 先頭部分列どうしなので単純に長い方) を採る (R18: 減らさない不変条件)。
 */
export function resolveKikiwakeState(logs: KikiwakeLogInput[], today: string): KikiwakeResolvedState {
  const parsed: ParsedLog[] = [];
  for (const l of logs) {
    const p = parseKikiwakeDetail(l.detail, CANON);
    if (!p) continue;
    parsed.push({ id: l.id, createdAt: l.createdAt, date: l.date, cfg: p.detail.cfg, seq: p.detail.seq, base: p.detail.base, trials: p.detail.trials, after: p.after });
  }
  parsed.sort((a, b) => (a.createdAt < b.createdAt ? -1 : a.createdAt > b.createdAt ? 1 : a.id < b.id ? -1 : 1));

  const first = parsed[0];
  if (first === undefined) {
    return { state: initialKikiwakeState(today), source: "initial", forked: false, nextSeq: 1, baseId: null };
  }

  const newest = lastOf(parsed);
  const prev = parsed.length >= 2 ? (parsed[parsed.length - 2] ?? null) : null;
  const lineageOk = newest.base === (prev?.id ?? null) && newest.seq === (prev ? prev.seq + 1 : 1);
  if (newest.after && lineageOk) {
    return { state: newest.after, source: "checkpoint", forked: false, nextSeq: newest.seq + 1, baseId: newest.id };
  }

  let state = initialKikiwakeState(first.date);
  for (const l of parsed) {
    state = applyKikiwakeSession(state, { date: l.date, trials: l.trials }, kikiwakeConfig(l.cfg));
  }
  // 減らさない不変条件: 有効なチェックポイントの最長 active を下回らない
  let longest: KikiwakeState | null = null;
  for (const l of parsed) {
    if (l.after && (!longest || l.after.active.length > longest.active.length)) longest = l.after;
  }
  if (longest && longest.active.length > state.active.length) {
    const active = [...longest.active];
    const addedAt = { ...state.addedAt };
    for (const c of active) {
      if (addedAt[c] === undefined) addedAt[c] = longest.addedAt[c] ?? today;
    }
    state = { ...state, active, addedAt };
  }
  const maxSeq = parsed.reduce((s, l) => Math.max(s, l.seq), 0);
  return { state, source: "replay", forked: Boolean(newest.after) && !lineageOk, nextSeq: maxSeq + 1, baseId: newest.id };
}
